import { twMerge } from "tailwind-merge";

import { Badge } from "@/components/badge";
import { Radio, RadioGroup } from "@/components/radio-group";

import type { Platform, PostType } from "./postTypes";

import { useSocialPages } from "../../../hooks/useSocialPages";
import { FacebookOutline, InstagramOutline } from "../PlatformOutlineIcons";
import { SelectionCheck } from "../SelectionCheck";
import { PostTypeIllustration } from "./PostTypeIllustration";
import { PLATFORM_LABELS, POST_TYPES } from "./postTypes";

const PLATFORMS: Platform[] = ["instagram", "facebook"];
const PLATFORM_ICONS = {
  facebook: FacebookOutline,
  instagram: InstagramOutline,
};

export function PostTypeStep({
  brandId,
  onSelectPostType,
  selectedPostType,
}: {
  brandId: number;
  onSelectPostType: (postType: PostType) => void;
  selectedPostType?: PostType;
}) {
  const { data } = useSocialPages();
  const connectedPlatforms = new Set(
    (data?.pages ?? [])
      .filter((page) => page.brand.id === brandId)
      .map((page) => page.platform),
  );

  function select(id: string) {
    const postType = POST_TYPES.find((postType) => postType.id === id);

    if (postType) onSelectPostType(postType);
  }

  return (
    <div className="flex flex-col gap-2xl">
      <div className="flex flex-col gap-xs">
        <h2 className="text-lg font-semibold text-text-primary">
          What do you want to post?
        </h2>
        <p className="text-sm text-text-tertiary">
          Pick the format. It decides where the post publishes and how it looks.
        </p>
      </div>

      <RadioGroup
        aria-label="Post type"
        className="flex flex-col gap-2xl"
        onChange={select}
        value={selectedPostType?.id ?? null}
      >
        {PLATFORMS.map((platform) => {
          const isConnected = connectedPlatforms.has(platform);
          const PlatformIcon = PLATFORM_ICONS[platform];

          return (
            <section className="flex flex-col gap-2xl" key={platform}>
              <div className="flex items-center gap-md">
                <PlatformIcon className="text-fg-secondary" />
                <h3 className="text-md font-semibold text-text-primary">
                  {PLATFORM_LABELS[platform]}
                </h3>
                {!isConnected && (
                  <Badge isRounded size="small" variant="primary">
                    Not connected for this brand
                  </Badge>
                )}
              </div>

              <div className="grid grid-cols-1 gap-2xl sm:grid-cols-3">
                {POST_TYPES.filter(
                  (postType) => postType.platform === platform,
                ).map((postType) => (
                  <Radio
                    className={twMerge(
                      "group cursor-pointer overflow-hidden rounded-2xl border border-border-secondary bg-bg-primary transition",
                      "hover:border-border-primary",
                      "data-selected:border-border-brand data-selected:ring-1 data-selected:ring-border-brand",
                      "data-disabled:cursor-not-allowed",
                    )}
                    isDisabled={!isConnected}
                    key={postType.id}
                    render={({ isSelected }) => (
                      <div className="flex flex-col">
                        <div className="relative flex justify-center border-b border-utility-brand-100 bg-utility-brand-50 py-7">
                          <PostTypeIllustration
                            className="h-[168px] transition-transform duration-300 ease-out motion-safe:group-hover:-translate-y-1 motion-safe:group-data-selected:-translate-y-1"
                            format={postType.format}
                          />
                          <SelectionCheck
                            className="absolute right-[15px] top-[14px] size-[26px] border-2"
                            isSelected={isSelected}
                          />
                        </div>
                        <div className="flex flex-col gap-xs p-2xl">
                          <span className="text-md font-semibold text-text-primary">
                            {PLATFORM_LABELS[platform]} {postType.label}
                          </span>
                          <span className="text-sm text-text-tertiary">
                            {postType.description}
                          </span>
                        </div>
                      </div>
                    )}
                    value={postType.id}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </RadioGroup>
    </div>
  );
}
